import { type FC, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import {
    trackEvent,
    type MethodTypeValues,
    eventNameConstants,
    methodTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { NftPdpCreatorItems } from './NftPdpCreatorItems';
import { ServerVarsContext } from '../../utils/ServerVarsContext';
import { Link } from 'dibs-elements/exports/Link';
import Facebook from 'dibs-icons/exports/legacy/Facebook';
import AnchorLink from 'dibs-icons/exports/legacy/AnchorLink';
import Instagram from 'dibs-icons/exports/legacy/Instagram';
import Twitter from 'dibs-icons/exports/legacy/Twitter';
import WikipediaW from 'dibs-icons/exports/legacy/WikipediaW';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle';
import { DiscordTooltip } from '../../components/DiscordTooltip';
import { profileLinks } from '../../helpers/profileHelpers';

import { type NftPdpCreator_nftItem$key } from './__generated__/NftPdpCreator_nftItem.graphql';
import { type NftPdpCreator_viewer$key } from './__generated__/NftPdpCreator_viewer.graphql';
import styles from './NftPdpCreator.scss';

const messages = defineMessages({
    web: { id: 'nft.pdp.creator.web', defaultMessage: 'Website' },
    instagram: { id: 'nft.pdp.creator.instagram', defaultMessage: 'Instagram' },
    facebook: { id: 'nft.pdp.creator.facebook', defaultMessage: 'Facebook' },
    twitter: { id: 'nft.pdp.creator.twitter', defaultMessage: 'Twitter' },
    wikipedia: { id: 'nft.pdp.creator.wikipedia', defaultMessage: 'Wikipedia' },
});

function trackSocialShare(label: MethodTypeValues): void {
    trackEvent({
        event: 'e_ga_event',
        category: 'share',
        action: 'NFT Creator from PDP',
        label,
        noninteraction: 0,
        eventName: eventNameConstants.EVENT_SHARE,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_NFT_CREATOR_FROM_PDP,
        method: label,
    });
}

type Props = {
    nftItem: NftPdpCreator_nftItem$key;
    viewer: NftPdpCreator_viewer$key;
};

export const NftPdpCreator: FC<Props> = ({ nftItem: nftItemRef, viewer: viewerRef }) => {
    const { isMobile } = useContext(ServerVarsContext);
    const { formatMessage } = useIntl();
    const viewer = useFragment(
        graphql`
            fragment NftPdpCreator_viewer on Viewer {
                ...NftPdpCreatorItems_viewer
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment NftPdpCreator_nftItem on NftItem {
                creatorAddress {
                    ...NftAvatarCircle_address
                    ...NftPdpCreatorItems_creatorAddress @arguments(itemCount: $creatorItemCount)
                    address
                    publicProfileUrl
                    publicProfile {
                        status
                        profileName
                        bio
                        webLink
                        instagramLink
                        twitterLink
                        facebookLink
                        wikipediaLink
                        discordLink
                    }
                }
            }
        `,
        nftItemRef
    );

    const publicProfile = nftItem.creatorAddress?.publicProfile;
    if (!publicProfile) {
        return null;
    }

    const creatorName = publicProfile.profileName;
    const shortBio = publicProfile.bio;
    const profileUrl = nftItem.creatorAddress.publicProfileUrl;
    const socialLinks: (keyof typeof publicProfile)[] = [
        'webLink',
        'instagramLink',
        'twitterLink',
        'facebookLink',
        'discordLink',
        'wikipediaLink',
    ];
    const isSocialLinksExist = socialLinks.some(socialLink => !!publicProfile[socialLink]);
    const shouldShowBioContainer = !!shortBio;
    const shouldShowMobileSocialContainer = isSocialLinksExist && !!isMobile;
    const shouldShowMiddleContainer = shouldShowBioContainer || shouldShowMobileSocialContainer;

    const socialIcons = (
        <>
            {!!publicProfile.webLink && (
                <Link
                    target="_blank"
                    className={styles.socialLink}
                    href={publicProfile.webLink}
                    onClick={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_WEBSITE)}
                >
                    <AnchorLink
                        className={styles.socialIcon}
                        ariaLabel={formatMessage(messages.web)}
                        role="img"
                    />
                </Link>
            )}
            {!!publicProfile.instagramLink && (
                <Link
                    target="_blank"
                    className={styles.socialLink}
                    href={profileLinks.instagram(publicProfile.instagramLink)}
                    onClick={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_INSTAGRAM)}
                >
                    <Instagram
                        className={styles.socialIcon}
                        ariaLabel={formatMessage(messages.instagram)}
                        role="img"
                    />
                </Link>
            )}
            {!!publicProfile.twitterLink && (
                <Link
                    target="_blank"
                    className={styles.socialLink}
                    href={profileLinks.twitter(publicProfile.twitterLink)}
                    onClick={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_TWITTER)}
                >
                    <Twitter
                        className={styles.socialIcon}
                        ariaLabel={formatMessage(messages.twitter)}
                        role="img"
                    />
                </Link>
            )}
            {!!publicProfile.facebookLink && (
                <Link
                    target="_blank"
                    className={styles.socialLink}
                    href={profileLinks.facebook(publicProfile.facebookLink)}
                    onClick={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_FACEBOOK)}
                >
                    <Facebook
                        className={styles.socialIcon}
                        ariaLabel={formatMessage(messages.facebook)}
                        role="img"
                    />
                </Link>
            )}
            {!!publicProfile.discordLink && (
                <DiscordTooltip
                    onOpen={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_DISCORD)}
                    linkClass={styles.socialLink}
                >
                    {publicProfile.discordLink}
                </DiscordTooltip>
            )}
            {!!publicProfile.wikipediaLink && (
                <Link
                    target="_blank"
                    className={styles.socialLink}
                    href={publicProfile.wikipediaLink}
                    onClick={() => trackSocialShare(methodTypeConstants.METHOD_TYPE_WIKIPEDIA)}
                >
                    <WikipediaW
                        className={styles.socialIcon}
                        ariaLabel={formatMessage(messages.wikipedia)}
                        role="img"
                    />
                </Link>
            )}
        </>
    );

    return (
        <div className={styles.section}>
            <div className={styles.topContainer}>
                <Link href={profileUrl || ''} buyerLinkType="linkNoDecoration">
                    <NftAvatarCircle
                        address={nftItem.creatorAddress}
                        wrapperClassName={styles.avatarContainer}
                        loading="lazy"
                        avatarSize={70}
                    />
                </Link>
                <div className={styles.nameContainer}>
                    <Link href={profileUrl || ''} buyerLinkType="standardLinkNoEffect">
                        <HeadingLevel>
                            {Header => <Header className={styles.nameHeader}>{creatorName}</Header>}
                        </HeadingLevel>
                    </Link>
                </div>
                {!isMobile && <div>{socialIcons}</div>}
            </div>
            {shouldShowMiddleContainer && (
                <div className={styles.middleContainer}>
                    {shouldShowBioContainer && (
                        <ExpandingAreaWithReadMore
                            wrapperClass={styles.bio}
                            collapsedHeight={96}
                            showGradient={false}
                        >
                            <span
                                data-tn="nft-pdp-creator-bio"
                                dangerouslySetInnerHTML={{
                                    __html: shortBio || '',
                                }}
                            />
                        </ExpandingAreaWithReadMore>
                    )}
                    {shouldShowMobileSocialContainer && (
                        <div className={styles.mobileSocialContainer}>{socialIcons}</div>
                    )}
                </div>
            )}
            {isMobile && (
                <NftPdpCreatorItems viewer={viewer} creatorAddress={nftItem.creatorAddress} />
            )}
        </div>
    );
};
